import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SketchPicker } from 'react-color';

import {
  SquareCrossed,
} from '../../assets/js/svg/gui';
import { useTownAssets } from '../../stores';
import { imageSrcFromId } from '../../utils';
import { Button } from '../buttons';
import {
  CardTitle,
  Column,
  FullPopup,
  Row,
} from '../display';
import CardSeparator from '../display/CardSeparator';
import { SecuredImg } from '../graphics';

import TextInput from './TextInput';

function ColorOrImagePicker(props) {
  const {
    id,
    initialValue,
    pickingDone,
    disabled,
    canBeCleared,
    types,
    uploadType,
  } = props;

  const [popupVisible, setPopupVisible] = useState(false);
  const [colorPicked, setColorPicked] = useState();
  const [imageIdPicked, setImageIdPicked] = useState();
  const [newFile, setNewFile] = useState();
  const [newFileLabel, setNewFileLabel] = useState('');
  const [newFileSts, setNewFileSts] = useState('ready');

  const { assets } = useTownAssets();
  const {
    translate,
    imagesList,
    fetchImagesList,
    uploadImage,
  } = assets;

  const isColor = (value) => (typeof value === 'string') && value.startsWith('#');

  const imagesFiltered = useMemo(
    () => {
      if (!imagesList) return undefined;

      return imagesList.filter((thisImage) => {
        if (!types) return true;

        return types.includes(thisImage.type);
      });
    },
    [imagesList, types],
  );

  const imagePicked = useMemo(
    () => {
      if (!imagesFiltered) return undefined;

      return imagesFiltered.find((thisImage) => (thisImage.id === imageIdPicked));
    },
    [imageIdPicked, imagesFiltered],
  );

  useEffect(
    () => {
      if (!imagesList) fetchImagesList();
    },
    [fetchImagesList, imagesList],
  );

  const handleClick = () => {
    if (disabled) return;

    setPopupVisible((prev) => {
      if (!prev) {
        if (isColor(initialValue)) {
          setColorPicked(initialValue);
          setImageIdPicked(undefined);
        } else if (initialValue) {
          setImageIdPicked(initialValue);
          setColorPicked(undefined);
        } else {
          setColorPicked(undefined);
          setImageIdPicked(undefined);
        }
      }
      return !prev;
    });
  };

  const cancel = () => {
    setPopupVisible(false);
    setNewFile();
    setNewFileLabel('');
    setNewFileSts('ready');
  };

  const fileOnInput = useCallback(
    async (event) => {
      if (event?.target?.files) {
        const file = event.target.files[0];

        event.target.value = null;
        setNewFile(file);
      }
    },
    [],
  );

  const addImage = useCallback(
    async () => {
      if (newFile) {
        setNewFileSts('busy');
        const fileInfo = {
          type: uploadType,
          label: newFileLabel.trim(),
        };

        const newImageId = await uploadImage(newFile, fileInfo);

        if (newImageId !== undefined) {
          setImageIdPicked(newImageId);
          setColorPicked(undefined);
          fetchImagesList();
          setNewFileSts('ready');
        } else {
          setNewFileSts('error');
        }
        setNewFile();
        setNewFileLabel('');
      }
    },
    [fetchImagesList, newFile, newFileLabel, uploadType, uploadImage],
  );

  const changeLabel = (event) => {
    setNewFileLabel(event.currentTarget.value);
  };

  const newFileLabelValid = newFileLabel
    ? newFileLabel.trim().length > 2
    : false;

  const done = () => {
    if (pickingDone) {
      const value = colorPicked || imageIdPicked;
      pickingDone({
        id,
        value,
      });
    }
    setPopupVisible(false);
    setNewFile();
    setNewFileLabel('');
    setNewFileSts('ready');
  };

  const clear = () => {
    setColorPicked(undefined);
    setImageIdPicked(undefined);
  };

  const handleColorChange = (aNewColor) => {
    setColorPicked(aNewColor.hex);
    setImageIdPicked(undefined);
    setNewFile();
    setNewFileLabel('');
  };

  const handleImageSelect = (imageId) => {
    setImageIdPicked(imageId);
    setColorPicked(undefined);
  };

  const hasSelection = colorPicked || imageIdPicked;

  const buttonContent = useMemo(
    () => {
      if (isColor(initialValue)) {
        return (
          <div
            className="w-full h-full rounded-xs"
            style={{ background: initialValue }}
          />
        );
      }

      if (initialValue) {
        return (
          <SecuredImg
            urlPath={imageSrcFromId(initialValue)}
            alt="img"
            className="h-full w-full object-contain"
          />
        );
      }

      return <SquareCrossed className="h-full w-full object-contain" />;
    },
    [initialValue],
  );

  return (
    <div className="relative">
      <button
        className={classNames(
          'w-11 h-7 p-0.5 rounded-sm relative shrink-0 bg-checkers bg-[size:8px]',
          {
            'border-2 border-pxPrimary cursor-pointer': !disabled,
            'border border-pxOutlineStrong/80': disabled,
          },
        )}
        onClick={handleClick}
        type="button"
      >
        {buttonContent}
      </button>

      {popupVisible && (
        <FullPopup
          onClose={cancel}
          titleBar={false}
          customSize
        >
          <Column
            className="pt-3"
            crossStart
            gap={8}
          >
            <Row fullw between start gap={4}>
              <CardTitle level={1} title={translate('colorOrImageSelection')} />
              <div className="flex-1" />
              <Row gap={3}>
                <span className="text-sm text-pxTxtMuted">
                  {`${translate('selection')}:`}
                </span>
                <div
                  className={classNames(
                    'w-12 h-12 rounded-sm bg-checkers bg-[size:8px] border',
                    {
                      'border-pxPrimary': hasSelection,
                      'border-pxOutlineStrong': !hasSelection,
                    },
                  )}
                >
                  {colorPicked && (
                    <div
                      className="w-full h-full rounded-xs"
                      style={{ background: colorPicked }}
                    />
                  )}
                  {imageIdPicked && (
                    <SecuredImg
                      urlPath={imageSrcFromId(imageIdPicked, true)}
                      alt="preview"
                      className="h-full w-full object-contain"
                    />
                  )}
                  {(!hasSelection) && (
                    <SquareCrossed className="h-full w-full object-contain text-pxTxtFaint" />
                  )}
                </div>
                {imagePicked && (
                  <Column crossStart gap={0}>
                    <span className="text-sm font-medium">{imagePicked.label}</span>
                    <span className="text-xs text-pxTxtMuted">
                      {`${imagePicked.width} × ${imagePicked.height} px`}
                    </span>
                    <span className="text-xs text-pxTxtMuted">
                      {`${imagePicked.filetype} - ${Math.round(imagePicked.size / 1024)} Ko`}
                    </span>
                  </Column>
                )}
                {colorPicked && (
                  <span className="text-sm font-mono">{colorPicked}</span>
                )}
              </Row>
            </Row>

            <Row
              className="flex-wrap"
              gap={6}
              crossStart
              start
            >
              <Column crossStart gap={2}>
                <CardTitle level={2} title={translate('color')} />
                <SketchPicker
                  color={colorPicked || '#FFFFFF'}
                  onChange={handleColorChange}
                  width={200}
                  styles={{
                    default: {
                      picker: {
                        boxShadow: 'none',
                        padding: '0',
                      },
                    },
                  }}
                />
              </Column>

              <Column crossStart gap={2} className="min-w-48">
                <CardTitle level={2} title={translate('image')} />
                <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
                  {imagesFiltered?.map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      className={classNames(
                        'w-12 h-12 p-0.5 rounded-sm bg-checkers bg-[size:8px] cursor-pointer',
                        {
                          'border-2 border-pxPrimary': imageIdPicked === image.id,
                          'border border-pxOutlineStrong': imageIdPicked !== image.id,
                        },
                      )}
                      onClick={() => handleImageSelect(image.id)}
                    >
                      <SecuredImg
                        urlPath={imageSrcFromId(image.id, true)}
                        alt={image.label}
                        className="h-full w-full object-contain"
                      />
                    </button>
                  ))}
                </div>

                <CardSeparator horizontal />

                <Column crossStart gap={1}>
                  <Row>
                    <Button
                      label={translate('file')}
                      id="selImage"
                      fileOnInput={fileOnInput}
                      disabled={newFileSts !== 'ready'}
                      accept="image/bmp, image/gif, image/jpeg, image/png, image/svg+xml, image/tiff, image/webp, image/x-windows-bmp"
                      baseColor
                    />
                    <span className="ml-2 text-sm">{newFile?.name}</span>
                  </Row>

                  <Row>
                    <span className="text-sm">
                      {`${translate('label')}:`}
                    </span>
                    <TextInput
                      id="label"
                      name="label"
                      value={newFileLabel}
                      onChange={changeLabel}
                      invalid={newFile && (!newFileLabelValid)}
                      className="w-32"
                    />
                    <Button
                      label={translate('add')}
                      id="addImage"
                      disabled={(newFileSts === 'busy') || (!newFileLabelValid)}
                      onClick={addImage}
                      pending={newFileSts === 'busy'}
                      baseColor
                    />
                  </Row>

                  {((!newFileLabelValid) && newFile) && (
                    <i className="text-pxUiAlert text-sm">{translate('pleaseNameThisFile')}</i>
                  )}

                  {(newFile?.size > (1024 * 1024 * 4.9)) && (
                    <i className="text-pxUiAlert text-sm">{translate('largeFilesRecompressed')}</i>
                  )}
                </Column>
              </Column>
            </Row>

            <Row fullw gap={8}>
              <Button
                imgSrc="/images/svg/gui/back.svg"
                onClick={cancel}
                neutralColor
              />
              {canBeCleared && (
                <Button
                  imgSrc="/images/svg/gui/trash.svg"
                  onClick={clear}
                  criticalColor
                />
              )}
              <Button
                imgSrc="/images/svg/gui/check.svg"
                onClick={done}
                disabled={(!canBeCleared) && (!hasSelection)}
                baseColor
              />
            </Row>
          </Column>
        </FullPopup>
      )}
    </div>
  );
}

ColorOrImagePicker.propTypes = {
  id: PropTypes.string,
  initialValue: PropTypes.string,
  pickingDone: PropTypes.func,
  disabled: PropTypes.bool,
  canBeCleared: PropTypes.bool,
  types: PropTypes.arrayOf(PropTypes.string),
  uploadType: PropTypes.string,
};

export default ColorOrImagePicker;
